import type { Priority, Size, StatusCategory } from "@beacon/core/projects"
import {
  ArchiveIcon,
  ArrowRightIcon,
  BotIcon,
  FolderInputIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  KeyRoundIcon,
  Link2Icon,
  MessageSquareIcon,
  PaperclipIcon,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  TagIcon,
  Trash2Icon,
  UnlinkIcon,
  UserMinusIcon,
  UserPlusIcon,
  ZapIcon,
} from "lucide-react"
import { Fragment, useState, type ReactNode } from "react"
import { Badge } from "@/primitives/badge.tsx"
import { Button } from "@/primitives/button.tsx"
import { describeAbsoluteTime, type Moment } from "@/time/describe.ts"
import type { Identity } from "./identity.ts"
import { Avatar, HueDot, PersonName, type PersonShown } from "./identity.tsx"
import { Key } from "./key.tsx"
import { BeaconMark } from "./marks.tsx"
import { RelativeTime } from "./relative-time.tsx"
import { RowActions, type RowAction } from "./row-actions.tsx"
import { LabelChip, PriorityIcon, SizeIcon, StatusIcon } from "./ticket-glyphs.tsx"
import { Toggletip } from "./toggletip.tsx"
import type { StatusStep } from "./ticket-glyphs.ts"
import { shownLines } from "./timeline.ts"
import { useViewport } from "./viewport.tsx"

/*
 * What happened to something, oldest first: a thin rail of changes, each one line saying who did
 * what and when, broken by comments, each a card. A ticket's, an Epic's and a Project's activity,
 * the Agent's model history and Spend's model changes all read this way. The page's adapter turns
 * its own records into entries: the composite knows nothing of tickets or models, only how a
 * change and a comment are shown.
 */

/**
 * What stands on the rail beside a change: the actor's avatar, a glyph for what changed, or the
 * `marker` number a chart marks the change with, so the line and the mark read as one. An `area`
 * is the dot of the hue something moved into, or an empty ring for none; a `size` is the Size
 * something was given, or the empty frame for none.
 */
export type EventGlyph =
  | { kind: "avatar" }
  | { kind: "marker"; number: number }
  | { kind: "status"; category: StatusCategory; step?: StatusStep }
  | { kind: "priority"; priority: Priority }
  | { kind: "size"; size: Size | null }
  | { kind: "area"; hue?: Identity }
  | { kind: keyof typeof eventIcons }

const eventIcons = {
  assign: UserPlusIcon,
  unassign: UserMinusIcon,
  link: Link2Icon,
  attach: PaperclipIcon,
  epic: ZapIcon,
  "pull-request": GitPullRequestIcon,
  "pull-request-unlinked": UnlinkIcon,
  merge: GitMergeIcon,
  label: TagIcon,
  edit: PencilIcon,
  create: PlusIcon,
  archive: ArchiveIcon,
  remove: Trash2Icon,
  move: FolderInputIcon,
} as const

/**
 * One piece of a change's sentence: plain words; `strong`, a value in the foreground (a title, a
 * Member); a `status`, as its circle and name; an `area`, as its hue's dot and name; a `label`, as
 * its chip; a `ticket`, as its key, a link with `href` (in a new tab when it `opens` there, as in a
 * ticket's Activity on the desk, with `state` for the page it opens in place); or a `change` from one thing to another, as two badges with an arrow between
 * (a model swapped).
 */
export type EventPart =
  | string
  | { strong: string }
  | { status: { category: StatusCategory; name: string; step?: StatusStep } }
  | { area: { name: string; hue: Identity } }
  | { label: { name: string; hue: Identity } }
  | { ticket: string; href?: string; opens?: "in-place" | "new-tab"; state?: unknown }
  | { change: { from: string; to: string } }

/**
 * How a change or a comment came, when not through the web app: the Slack thread Beacon made it in
 * for its actor, the connected agent or access token that made it for them, a pull request on
 * GitHub, or the embedded agent whose plan its actor created, named as `label` says.
 */
export type TimelineVia =
  | { kind: "slack"; label: string; thread: string }
  | { kind: "connected-agent"; label: string; unverifiedName: boolean }
  | { kind: "access-token"; label: string }
  | { kind: "github"; label: string }
  | { kind: "embedded-agent"; label: string }

/**
 * A change: who made it (by name, `beacon` for Beacon itself, or null for a former Member), what it
 * was, and when. `via` says how it came, when not through the web app.
 */
export type TimelineEvent = {
  kind: "event"
  id: string
  actor: PersonShown | "beacon" | null
  glyph?: EventGlyph
  parts: readonly EventPart[]
  at: Moment
  via?: TimelineVia
}

/**
 * A comment: its author (null for a former Member), when, whether it was edited since, its `body`
 * (the rendered markdown, or the editor while its author edits it), any `attachments` its text
 * doesn't show, and the `actions` its menu offers (Edit and Delete only for its author).
 */
export type TimelineComment = {
  kind: "comment"
  id: string
  author: PersonShown | null
  at: Moment
  editedAt?: Moment
  via?: TimelineVia
  body: ReactNode
  attachments?: ReactNode
  actions?: readonly RowAction[]
  /** The card's id, for a link to land on it, as `#comment-<id>`. */
  anchor?: string
}

export type TimelineEntry = TimelineEvent | TimelineComment

/**
 * The entries in order, on a rail. With `collapseRuns`, four or more changes in a row show their
 * first and last, with "Show n more" between them.
 */
export function Timeline({
  entries,
  collapseRuns = false,
}: {
  entries: readonly TimelineEntry[]
  collapseRuns?: boolean
}) {
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set())
  const lines = shownLines(entries, { collapseRuns, opened })
  return (
    <div className="relative">
      <div aria-hidden className="absolute top-0 bottom-0 left-3 w-px bg-border-subtle" />
      <ol className="relative">
        {lines.map((line) =>
          line.kind === "more" ? (
            <li key={`more-${line.run}`} className="flex min-h-7 items-center gap-2 py-1">
              <span aria-hidden className="size-6 shrink-0" />
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setOpened((now) => new Set([...now, line.run]))}
              >
                Show {line.hidden} more
              </Button>
            </li>
          ) : line.entry.kind === "event" ? (
            <EventLine key={`event-${line.entry.id}`} event={line.entry} />
          ) : (
            <CommentCard key={`comment-${line.entry.id}`} comment={line.entry} />
          ),
        )}
      </ol>
    </div>
  )
}

function ViaNote({ via }: { via: TimelineVia }) {
  if (via.kind === "connected-agent")
    return (
      <span className="inline-flex items-center gap-1 text-subtle-foreground">
        <BotIcon aria-hidden className="size-3" />
        {via.label}
        {via.unverifiedName && <span className="text-warning-text">(unverified name)</span>}
      </span>
    )
  if (via.kind === "access-token")
    return (
      <span className="inline-flex items-center gap-1 text-subtle-foreground">
        <KeyRoundIcon aria-hidden className="size-3" />
        {via.label}
      </span>
    )
  if (via.kind === "github")
    return (
      <span className="inline-flex items-center gap-1 text-subtle-foreground">
        <GitPullRequestIcon aria-hidden className="size-3" />
        {via.label}
      </span>
    )
  if (via.kind === "embedded-agent")
    return (
      <span className="inline-flex items-center gap-1 text-subtle-foreground">
        <SparklesIcon aria-hidden className="size-3" />
        {via.label}
      </span>
    )
  return (
    <a
      href={via.thread}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-sm text-subtle-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring"
    >
      <MessageSquareIcon aria-hidden className="size-3" />
      {via.label}
    </a>
  )
}

function Glyph({ glyph, actor }: { glyph: EventGlyph; actor: TimelineEvent["actor"] }) {
  switch (glyph.kind) {
    case "avatar":
      return actor === "beacon" ? <BeaconMark size={20} /> : <Avatar person={actor} size={20} />
    case "status":
      return <StatusIcon category={glyph.category} {...(glyph.step && { step: glyph.step })} />
    case "priority":
      return <PriorityIcon priority={glyph.priority} />
    case "size":
      return <SizeIcon size={glyph.size} />
    case "area":
      return <HueDot hue={glyph.hue} />
    case "marker":
      return (
        <span className="grid size-4 place-items-center rounded-full text-2xs font-medium text-foreground tabular-nums ring-1 ring-spend-model-change">
          {glyph.number}
        </span>
      )
    default: {
      const Icon = eventIcons[glyph.kind]
      return <Icon aria-hidden className="size-3.5 text-muted-foreground" />
    }
  }
}

function Part({ part }: { part: EventPart }) {
  if (typeof part === "string") return part
  if ("strong" in part) return <span className="font-medium text-foreground">{part.strong}</span>
  if ("ticket" in part)
    return (
      <Key
        value={part.ticket}
        {...(part.href && { href: part.href })}
        {...(part.opens && { opens: part.opens })}
        {...(part.state !== undefined && { state: part.state })}
      />
    )
  if ("status" in part)
    return (
      <span className="inline-flex items-center gap-1 align-top font-medium text-foreground">
        <StatusIcon
          category={part.status.category}
          {...(part.status.step && { step: part.status.step })}
        />
        {part.status.name}
      </span>
    )
  if ("area" in part)
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
        <HueDot hue={part.area.hue} />
        {part.area.name}
      </span>
    )
  if ("label" in part)
    return (
      <span className="inline-flex max-w-full align-top">
        <LabelChip name={part.label.name} hue={part.label.hue} />
      </span>
    )
  // On `compact` a change too wide for the line breaks after its arrow, rather than running out of
  // its card.
  return (
    <span className="inline-flex items-center gap-1 align-top compact:max-w-full compact:flex-wrap compact:gap-y-1">
      <Badge variant="neutral">{part.change.from}</Badge>
      <ArrowRightIcon aria-hidden className="size-3 text-subtle-foreground" />
      <Badge variant="neutral">{part.change.to}</Badge>
    </span>
  )
}

function EventLine({ event }: { event: TimelineEvent }) {
  // On `compact` a dot stays with the moment or the way it introduces when the line wraps, rather
  // than ending a line by itself; the desk's lines break as they always have.
  const dot = useViewport().compact ? " ·\u00a0" : " · "
  return (
    <li className="flex min-h-7 items-start gap-2 py-1">
      {/* The glyph's backdrop hides the rail behind it in the fill of what it rests on, a card or
          a popover as much as the page, so it never shows as a square of its own. */}
      <span className="relative z-10 grid size-6 shrink-0 place-items-center rounded-full bg-background in-data-[slot=card]:bg-card in-data-[slot=popover-content]:bg-popover">
        <Glyph glyph={event.glyph ?? { kind: "avatar" }} actor={event.actor} />
      </span>
      <p className="min-w-0 flex-1 pt-0.5 text-xs leading-5 text-muted-foreground">
        <span className="font-medium text-foreground">
          {event.actor === "beacon" ? "Beacon" : <PersonName person={event.actor} />}
        </span>{" "}
        {event.parts.map((part, index) => (
          <Fragment key={index}>
            {index > 0 && " "}
            <Part part={part} />
          </Fragment>
        ))}
        <span className="text-subtle-foreground">
          {dot}
          <RelativeTime at={event.at} />
        </span>
        {event.via && (
          <>
            {dot}
            <ViaNote via={event.via} />
          </>
        )}
      </p>
    </li>
  )
}

function CommentCard({ comment }: { comment: TimelineComment }) {
  return (
    <li id={comment.anchor} className="flex scroll-mt-16 items-start gap-2 py-2">
      <span className="relative z-10 grid size-6 shrink-0 place-items-center rounded-full bg-background in-data-[slot=card]:bg-card in-data-[slot=popover-content]:bg-popover">
        <Avatar person={comment.author} size={24} />
      </span>
      <article className="group/row min-w-0 flex-1 rounded-lg bg-card ring-1 ring-border">
        {/* On `compact` what says who wrote it, when and how wraps onto more lines, its ⋯ level
            with the first; on the desk the line is one row. */}
        <header className="flex h-9 items-center gap-2 px-3 text-xs text-subtle-foreground compact:h-auto compact:min-h-9 compact:items-start compact:py-1.5">
          <span className="contents compact:flex compact:min-w-0 compact:flex-1 compact:flex-wrap compact:items-baseline compact:gap-x-2 compact:pt-0.5">
            <span className="text-ui font-medium text-foreground compact:min-w-0 compact:wrap-break-word">
              <PersonName person={comment.author} />
            </span>
            <RelativeTime at={comment.at} />
            {comment.editedAt !== undefined && (
              <Toggletip tip={`Edited ${describeAbsoluteTime(comment.editedAt)}`}>
                <span>edited</span>
              </Toggletip>
            )}
            {comment.via && <ViaNote via={comment.via} />}
          </span>
          {comment.actions && comment.actions.length > 0 && (
            <span className="ml-auto">
              <RowActions items={comment.actions} label="Comment actions" />
            </span>
          )}
        </header>
        <div className="px-3 pb-3 text-prose">{comment.body}</div>
        {comment.attachments && <div className="px-3 pb-3">{comment.attachments}</div>}
      </article>
    </li>
  )
}
